/**
 * Favorites Context - Manages user's favorite ads with localStorage persistence
 * Provides cross-tab synchronization for favorite state
 */

import { createContext, useContext, useMemo, ReactNode } from "react";
import { useLocalStorageState } from "../hooks/useLocalStorageState";

/**
 * Context value interface for favorites management
 * @property favorites - Array of favorite ad IDs
 * @property isFavorite - Check if an ad is favorited
 * @property toggleFavorite - Add or remove an ad from favorites
 */
interface FavoritesContextValue {
  favorites: string[];
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

const STORAGE_KEY = "ad_favorites";

/**
 * Favorites Provider Component
 * Wraps the application to provide favorite ad management functionality.
 * Persists favorites to localStorage and synchronizes across browser tabs.
 *
 * @param children - React children to be wrapped by the provider
 *
 * @example
 * // Setup in app root
 * import { FavoritesProvider } from './context/FavoritesContext';
 *
 * function App() {
 *   return (
 *     <FavoritesProvider>
 *       <YourApp />
 *     </FavoritesProvider>
 *   );
 * }
 *
 * @example
 * // Using the hook in a component
 * import { useFavorites } from './context/FavoritesContext';
 *
 * function AdCard({ ad }) {
 *   const { isFavorite, toggleFavorite } = useFavorites();
 *
 *   return (
 *     <div>
 *       <h3>{ad.title}</h3>
 *       <button onClick={() => toggleFavorite(ad.id)}>
 *         {isFavorite(ad.id) ? '★ Favorited' : '☆ Favorite'}
 *       </button>
 *     </div>
 *   );
 * }
 */
export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favorites, setFavorites] = useLocalStorageState<string[]>(STORAGE_KEY, []);

  const value = useMemo<FavoritesContextValue>(() => ({
    favorites,
    isFavorite: (id: string) => favorites.includes(id),
    toggleFavorite: (id: string) =>
      setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
  }), [favorites]);

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

/**
 * Hook to access favorites context
 * Must be used within a FavoritesProvider.
 *
 * @returns FavoritesContextValue with current favorites state and actions
 * @throws Error if used outside of FavoritesProvider
 *
 * @example
 * function FavoriteList() {
 *   const { favorites, isFavorite, toggleFavorite } = useFavorites();
 *
 *   return (
 *     <div>
 *       <h2>My Favorites ({favorites.length})</h2>
 *       {favorites.map(id => (
 *         <div key={id}>
 *           Ad ID: {id}
 *           <button onClick={() => toggleFavorite(id)}>Remove</button>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 */
export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used within FavoritesProvider");
  return ctx;
};
